import { Suspense, type ElementType } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { PageViewGate } from '@/components/auth/PageViewGate';
import {
  SETTINGS_PATH,
  getSettingsCategory,
  getSettingsMastersByCategory,
  isSettingsMasterVisibleInModule,
  type SettingsComponentKey,
} from '@/config/settingsRegistry';
import { useActiveModule } from '@/hooks/useActiveModule';
import { moduleSubscriptionLink } from '@/config/moduleDefaults';
import { useActiveSettingsModuleKey, useCanViewSettingsMaster } from './settingsAccess';
import { SettingsComingSoonPage } from './SettingsComingSoonPage';
import { SettingsMasterSkeleton } from './SettingsMasterSkeleton';
import {
  ApprovalBuilderPage,
  ApprovalDelegationsPage,
  ApprovalMatrixPage,
  BrandsPage,
  CategoriesPage,
  CompaniesPage,
  CrmCompaniesPage,
  CrmContactsPage,
  CrmSetupPage,
  CustomersPage,
  DeskBoardsPage,
  DeskCannedResponsesPage,
  DeskKnowledgeBasePage,
  DeskSetupPage,
  DeskWorkflowDesignerPage,
  DiscountsPage,
  EmployeesPage,
  FinanceAuditTrailPage,
  FinanceBusinessRulesPage,
  FinanceLedgerPage,
  FinanceAccountingPage,
  FinanceAdministrationWorkspacePage,
  FinanceAutomationPage,
  FinanceCloseCenterPage,
  FinanceHealthCenterPage,
  FinanceIntegrationsPage,
  FinanceSetupPage,
  FinanceTaxesPage,
  FinanceWorkflowsPage,
  HrAdminConfigPage,
  HrAllowanceTypesPage,
  HrMasterDataSettingsPage,
  HrOrgStructurePage,
  HrSetupPage,
  InventoryLedgerPage,
  InventoryPage,
  LeadGenSourcesPage,
  MarketingAcquisitionBuildersPage,
  MarketingEspWebhooksPage,
  MarketingSegmentsPage,
  MarketingSuppressionPage,
  MarketingTemplatesPage,
  NotificationsCenterPage,
  OrganizationProfilePage,
  ProductsPage,
  ProjectsAutomationPage,
  ProjectsBoardsPage,
  ProjectsIssuesPage,
  ProjectsResourcesPage,
  ProjectsSetupPage,
  RolesPage,
  SocialConnectionCenterPage,
  SocialSettingsHubPage,
  SocialWebhooksPage,
  StoreProductsPage,
  StoresPage,
  TaxRulesPage,
  TranslationsPage,
  UnitsPage,
  WarehouseSetupPage,
} from '@/routes/lazyPages';

function ModuleSubscriptionRedirect() {
  const activeModule = useActiveModule() ?? 'POS';
  return <Navigate to={moduleSubscriptionLink(activeModule)} replace />;
}

const COMPONENTS: Record<Exclude<SettingsComponentKey, 'genericMasterPlaceholder'>, ElementType> = {
  organizationProfile: OrganizationProfilePage,
  companies: CompaniesPage,
  employees: EmployeesPage,
  roles: RolesPage,
  stores: StoresPage,
  products: ProductsPage,
  categories: CategoriesPage,
  brands: BrandsPage,
  units: UnitsPage,
  taxRules: TaxRulesPage,
  storeProducts: StoreProductsPage,
  discounts: DiscountsPage,
  inventory: InventoryPage,
  inventoryLedger: InventoryLedgerPage,
  warehouseSetup: WarehouseSetupPage,
  hrSetup: HrSetupPage,
  hrAllowanceTypes: HrAllowanceTypesPage,
  hrMasterDataSettings: HrMasterDataSettingsPage,
  hrOrg: HrOrgStructurePage,
  hrAdmin: HrAdminConfigPage,
  financeSetup: FinanceSetupPage,
  financeTaxes: FinanceTaxesPage,
  financeRules: FinanceBusinessRulesPage,
  financeAudit: FinanceAuditTrailPage,
  financeWorkflows: FinanceWorkflowsPage,
  financeLedger: FinanceLedgerPage,
  financeControl: FinanceAdministrationWorkspacePage,
  financeHealth: FinanceHealthCenterPage,
  financeClose: FinanceCloseCenterPage,
  financeAutomation: FinanceAutomationPage,
  financeIntegrations: FinanceIntegrationsPage,
  financeAccounting: FinanceAccountingPage,
  crmSetup: CrmSetupPage,
  crmContacts: CrmContactsPage,
  crmCompanies: CrmCompaniesPage,
  customers: CustomersPage,
  leadGenSources: LeadGenSourcesPage,
  marketingTemplates: MarketingTemplatesPage,
  marketingSegments: MarketingSegmentsPage,
  marketingSuppression: MarketingSuppressionPage,
  marketingWebhooks: MarketingEspWebhooksPage,
  marketingAcquisition: MarketingAcquisitionBuildersPage,
  projectsSetup: ProjectsSetupPage,
  projectsBoards: ProjectsBoardsPage,
  projectsIssues: ProjectsIssuesPage,
  projectsResources: ProjectsResourcesPage,
  projectsAutomation: ProjectsAutomationPage,
  deskSetup: DeskSetupPage,
  deskWorkflow: DeskWorkflowDesignerPage,
  deskCannedResponses: DeskCannedResponsesPage,
  deskKnowledgeBase: DeskKnowledgeBasePage,
  deskBoards: DeskBoardsPage,
  approvalsBuilder: ApprovalBuilderPage,
  approvalsMatrix: ApprovalMatrixPage,
  approvalDelegations: ApprovalDelegationsPage,
  notificationsCenter: NotificationsCenterPage,
  socialConnect: SocialConnectionCenterPage,
  socialSettings: SocialSettingsHubPage,
  socialWebhooks: SocialWebhooksPage,
  translations: TranslationsPage,
  moduleSubscription: ModuleSubscriptionRedirect,
};

export function SettingsMasterRoute() {
  const { categoryKey, masterKey } = useParams();
  const scopeModule = useActiveSettingsModuleKey();
  const canView = useCanViewSettingsMaster();
  const category = getSettingsCategory(categoryKey);
  const master = category
    ? getSettingsMastersByCategory(category.key).find((item) => item.key === masterKey)
    : undefined;

  if (!category || !master) {
    return <Navigate to={SETTINGS_PATH} replace />;
  }

  if (!isSettingsMasterVisibleInModule(master, scopeModule) || !canView(master)) {
    return <Navigate to={SETTINGS_PATH} replace />;
  }

  if (master.componentKey === 'genericMasterPlaceholder') {
    return (
      <div className="page-content w-full min-w-0">
        <PageViewGate path={master.route}>
          <SettingsComingSoonPage master={master} />
        </PageViewGate>
      </div>
    );
  }

  const Component = COMPONENTS[master.componentKey];

  return (
    <div className="page-content w-full min-w-0">
      <PageViewGate path={master.route}>
        <Suspense fallback={<SettingsMasterSkeleton />}>
          <Component />
        </Suspense>
      </PageViewGate>
    </div>
  );
}
